import React from 'react';
import { FieldBuilderRedesigned as FieldBuilder } from 'new/elements/FieldBuilderRedesigned/FieldBuilder';
import { get, has, isEmpty } from 'lodash';
import { Stack } from 'new/elements/Stack';
import { Checkbox, FormControlLabel } from '@mui/material';
import DebouncedText from 'js/components/elements/editors/mui/DebouncedText';
import { convertToValidDBColumnName } from 'js/lib/ValidationUtils';
import { Switch } from 'new/elements/Switch';
import { Box } from 'new/elements/Box';
import Typography from 'new/elements/Typography';
import classnames from 'classnames';

const TableUpdateMode = ({
  connectionMeta,
  sinkConfig,
  insertModeField,
  table,
  trackerMode,
  selectedConnection,
  editSelectedTable,
  onAutoModeChanged,
  onTrackerModeChanged,
  onTrackerNameChanged,
  containerClassName = '',
}) => {
  if (isEmpty(table)) return null;
  const { allowSinkAutoMapping } = connectionMeta;

  const isUpsertMode = table['insert.mode'] === 'UPSERT';

  const isTrackerModeSet = trackerMode === 'RECORD';

  const isAutoMode = !!(
    allowSinkAutoMapping &&
    !isUpsertMode &&
    sinkConfig.mapping &&
    sinkConfig.mapping.mode === 'auto'
  );

  const insertModeSelectorField =
    allowSinkAutoMapping && isAutoMode
      ? {
          ...insertModeField,
          disabled: true,
          description: `${insertModeField.description}\n\n Upsert mode cannot be applied if automatic mapping is enabled.\n\n Please deselect the option for automatically mapping attributes to database columns if you wish to perform database upserts.`,
        }
      : { ...insertModeField, display_name: '' };

  return (
    <Box className={classnames('box', containerClassName)} title="Update mode">
      <Stack padding="16px" direction="row" gap="16px" alignItems="center">
        <div className="table-field-wrapper" style={{ marginBottom: 0 }}>
          <FieldBuilder
            key={insertModeField.value}
            field={insertModeSelectorField}
            currentFieldValue={table['insert.mode'] || insertModeField.default}
            isRequired={true}
            id="insert.mode"
            fieldLabelClassName="adv-settings-sink-insert.mode adv-settings-database-insert.mode"
            onChange={editSelectedTable}
            showRequiredIndicator={false}
          />
        </div>
        {allowSinkAutoMapping && !isUpsertMode && (
          <div className="table-field-wrapper" style={{ marginBottom: 0 }}>
            <FormControlLabel
              label="Automatically map attributes to database columns"
              control={<Checkbox checked={isAutoMode} onChange={onAutoModeChanged} />}
            />
          </div>
        )}
        {isUpsertMode && (
          <div className="table-field-wrapper" style={{ marginBottom: 0 }}>
            <Stack direction="row" alignItems="center">
              <Checkbox
                inputProps={{
                  'data-testid': 'checkbox-upsert-nulls',
                }}
                key="upsert-nulls"
                id="upsert-nulls"
                className="table-editor-checkbox adv-settings-sink-upsert.nulls adv-settings-database-upsert.nulls"
                checked={has(table, 'upsert.nulls') ? table['upsert.nulls'] : true}
                onChange={(ev, value) => {
                  editSelectedTable('upsert.nulls', value);
                }}
              />
              <Stack>
                <Typography variant="bodyDefault">Allow column updates with nulls</Typography>
                <Typography variant="bodySmall">
                  Uncheck to allow partial upsert of a record with only non-null values
                </Typography>
              </Stack>
            </Stack>
          </div>
        )}
        <Stack
          sx={{
            opacity: isAutoMode ? 0 : 1,
            visibility: isAutoMode ? 'hidden' : 'visible',
            transition: 'opacity 0.2s ease-in-out',
            pointerEvents: isAutoMode ? 'none' : 'auto',
            // Preserve space when hidden to prevent layout shift
            flexShrink: 0,
          }}
        >
          <Stack className="track-lineage-container" direction="row" alignItems="center">
            <FormControlLabel
              label="Track lineage"
              control={
                <Switch
                  id="set-tracker-checkbox"
                  checked={isTrackerModeSet}
                  onChange={onTrackerModeChanged}
                  disabled={isAutoMode}
                />
              }
            />
            <DebouncedText
              disabled={!isTrackerModeSet || isAutoMode}
              data-testid="tracker-name-input"
              key="tracker_name"
              debounceTimeMs={50}
              value={get(
                sinkConfig,
                'mapping.tracker_name',
                convertToValidDBColumnName('__nexla_tracker_id', selectedConnection),
              )}
              onChange={onTrackerNameChanged}
            />
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
};

export default TableUpdateMode;
